/**
 * Editor для ref.pos_providers (Порція Роздріб.5b).
 * Особливість: кнопка «Тест підключення» → GET /test-connection.
 * Config — JSON textarea (мінімальний UI; повна форма за provider_type — фаза 2).
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailPosProviders,
  type PosProviderType,
} from '@/api/retailPosProviders';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useNextRefCode } from '@/hooks/useNextRefCode';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import { useFormDraft } from '@/shared/hooks/useFormDraft';

const PERMISSION = 'retail.pos_provider.manage';

interface FormState {
  organization_id: string;
  code: string;
  name: string;
  provider_type: PosProviderType;
  is_connected: boolean;
  is_active: boolean;
  config: string; // JSON text
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  code: '',
  name: '',
  provider_type: 'mock',
  is_connected: false,
  is_active: true,
  config: '{}',
  comment: '',
};

export default function PosProviderEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);

  const docQuery = useQuery({
    queryKey: ['retail-pos-provider', id],
    queryFn: () => retailPosProviders.get(id!),
    enabled: !isNew,
  });

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  // Авто-пропозиція коду PRRO-NN
  const nextCode = useNextRefCode({
    queryKey: ['retail-pos-providers', form.organization_id, 'for-next-code'],
    queryFn: () =>
      retailPosProviders.list({ organization_id: form.organization_id, limit: 500 }),
    extractCodes: (data) => data.items.map((i) => i.code),
    prefix: 'PRRO',
    enabled: isNew && !!form.organization_id,
  });
  useEffect(() => {
    if (isNew && nextCode.suggested && !form.code) {
      setForm((f) => ({ ...f, code: nextCode.suggested }));
    }
  }, [isNew, nextCode.suggested, form.code]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        code: d.code,
        name: d.name,
        provider_type: d.provider_type,
        is_connected: d.is_connected,
        is_active: d.is_active,
        config: JSON.stringify(d.config ?? {}, null, 2),
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const parseConfig = (): Record<string, unknown> => {
    try {
      return JSON.parse(form.config || '{}');
    } catch {
      throw new Error('Невалідний JSON у полі Config');
    }
  };

  const payload = () => ({
    code: form.code,
    name: form.name,
    provider_type: form.provider_type,
    is_connected: form.is_connected,
    is_active: form.is_active,
    config: parseConfig(),
    comment: form.comment || null,
  });

  const create = useMutation({
    mutationFn: () => {
      const p = payload();
      return retailPosProviders.create({ organization_id: form.organization_id, ...p });
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-pos-providers'] });
      navigate(`/retail/pos-providers/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () => retailPosProviders.update(id!, payload()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-pos-providers'] });
      qc.invalidateQueries({ queryKey: ['retail-pos-provider', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailPosProviders.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-pos-providers'] });
      navigate('/retail/pos-providers');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const test = useMutation({
    mutationFn: () => retailPosProviders.testConnection(id!),
    onSuccess: (r) => {
      setTestResult(
        `${r.success ? '✓ Успіх' : '⚠ Помилка'} (${r.last_test_status})${
          r.last_test_message ? ': ' + r.last_test_message : ''
        }`,
      );
      qc.invalidateQueries({ queryKey: ['retail-pos-provider', id] });
    },
    onError: (e) => {
      setTestResult(`⚠ ${extractApiError(e)}`);
    },
  });

  const handleSave = () => {
    setError(null);
    try {
      parseConfig();
    } catch (err) {
      setError(extractApiError(err));
      return;
    }
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        code: form.code,
        name: form.name,
        provider_type: form.provider_type,
      },
      {
        organization_id: 'Юрособа',
        code: 'Код',
        name: 'Назва',
        provider_type: 'Тип',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий ПРРО-провайдер' : `ПРРО: ${form.name} — ${form.code}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/pos-providers')}>Скасувати</button>
          <RequirePermission code={PERMISSION}>
            <button className="primary" onClick={handleSave} disabled={create.isPending || update.isPending}>
              Зберегти
            </button>
          </RequirePermission>
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button onClick={() => test.mutate()} disabled={test.isPending}>
                Тест підключення
              </button>
            </RequirePermission>
          )}
          {!isNew && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити провайдер?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name} — {o.code}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Код</RequiredLabel>
          <input
            value={form.code}
            onChange={(e) => setForm({ ...form, code: e.target.value })}
            maxLength={30}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Назва</RequiredLabel>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            maxLength={255}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <RequiredLabel>Тип</RequiredLabel>
          <select
            value={form.provider_type}
            onChange={(e) =>
              setForm({ ...form, provider_type: e.target.value as PosProviderType })
            }
            disabled={!isNew}
          >
            <option value="checkbox">Checkbox</option>
            <option value="vchasno">Вчасно</option>
            <option value="mock">Демо (mock)</option>
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            <input
              type="checkbox"
              checked={form.is_connected}
              onChange={(e) => setForm({ ...form, is_connected: e.target.checked })}
            />
            {' '}Підключено
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 6' }}>
          <label>
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {' '}Активний
          </label>
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Config (JSON)</label>
          <textarea
            value={form.config}
            onChange={(e) => setForm({ ...form, config: e.target.value })}
            rows={6}
            style={{ fontFamily: 'monospace' }}
          />
          <small style={{ color: 'var(--text-secondary)' }}>
            Залежить від типу провайдера. Для checkbox — license_key, для
            vchasno — api_token тощо. Mock — порожній.
          </small>
        </div>
        <div className="field" style={{ gridColumn: 'span 12' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
          />
        </div>
      </div>

      {testResult && (
        <div style={{ marginTop: '1rem', color: testResult.startsWith('✓') ? '#15803d' : '#b91c1c' }}>
          {testResult}
        </div>
      )}
      {error && (
        <div className="error" style={{ marginTop: '1rem', color: 'var(--badge-red-fg)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
